import React, { useEffect, useState } from "react";
import styled from "styled-components/macro";
import { compose } from "ramda";
import { RouteComponentProps } from "react-router-dom";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import axios from "axios";

import { Typography, showToast } from "@deeptrue-frontend/ui";

import { OrganizationType } from "store/modules/auth/auth";
import api from "api/api";

import { parseErrorsToFormik, errorsValidation } from "components/Forms/utils";
import { ReactComponent as WarnIcon } from "../../assets/icons/warning-triangle.svg";
import BillingInfo, {
  SubmitHandler,
} from "components/Forms/BillingInfo/BillingInfo";
import { useFormatMessage } from "components/IntlProvider";

import StoreProvider from "components/StoreProvider/StoreProvider";
import env from "consts/env";

const { connect, selectors } = StoreProvider;

const StyledCardBlock = styled.div`
  display: block !important;
  width: 100%;
  padding: 0 1.5rem;
`;

const Notification = styled.div`
  background-color: #fff9e5;
  padding: 1.375rem;
  display: flex;
  align-items: center;
  margin-top: 2rem;
`;

interface OrganizationAddressInfo {
  id: number;
  address: string;
  postal_code: string;
  city: string;
}

interface CreditCardInfoProps extends RouteComponentProps<any> {
  organization: OrganizationType;
  initialCredits: string;
  totalProjectCostValue: string;
  successPath: string;
  user: any;
  onCancel: () => void;
}
interface LimitData {
  limit: number;
  available_limit: number;
  reserved_tokens: number;
  available_tokens: number;
}

const CreditCard = ({
  organization,
  user,
  initialCredits,
  totalProjectCostValue,
  successPath,
  onCancel,
}: CreditCardInfoProps) => {
  const [limitsRes, setLimitsRes] = useState<LimitData>({
    limit: 0,
    available_limit: 0,
    reserved_tokens: 0,
    available_tokens: 0,
  });
  const [addressInfoRes, setAddressInfoRes] = useState<
    OrganizationAddressInfo[]
  >([]);
  const [chosenAddress, setChosenAddress] = useState({
    address: "",
    postal_code: "",
    city: "",
    id: -1,
  });
  const t = useFormatMessage();

  useEffect(() => {
    axios
      .all([
        api.resources.getOrganizationInfo(organization.codename),
        api.resources.getOrganizationPaymentLimits(organization.codename),
      ])
      .then(
        axios.spread((info, limits) => {
          setAddressInfoRes(info.data.results.slice(0, 4));
          setLimitsRes(limits.data);
        })
      );
  }, [organization.codename]);

  const initialValues = {
    id: -1,
    quantity: initialCredits,
    company_name: organization.name,
    first_name: user.first_name,
    last_name: user.last_name,
    address: "",
    postal_code: "",
    country: "",
    type: "company",
    contact_phone: "",
    additional_address: "",
    city: "",
    contact_email: user.email,
    company_number: "",
  };

  const removeAddress = (id: number) =>
    api.resources
      .deletePaymentAddress({
        organization_codename: organization.codename,
        id,
      })
      .then(() => {
        showToast({
          type: "success",
          title: t(
            "success.toast.deleteAddress",
            "The address has been deleted"
          ),
        });
        setChosenAddress({ ...chosenAddress, id: -1 });
        api.resources
          .getOrganizationInfo(organization.codename)
          .then(({ data }) => {
            setAddressInfoRes(data.results.slice(0, 4));
          });
      })
      .catch(() => {
        showToast({
          type: "error",
          title: t(
            "errors.toast.deleteAddress",
            "We cannot delete the address"
          ),
        });
      });

  const onSubmit: SubmitHandler = async (values, formHelpers) => {
    const stripePromise = loadStripe(env.STRIPE_KEY || "");
    localStorage.projectRedirectionAfterPayment = successPath;

    if (chosenAddress.id !== -1) {
      return api.resources
        .createPayment({
          organization_codename: organization.codename,
          data: {
            address_info: chosenAddress.id,
            quantity: values.quantity,
            type: 1,
          },
        })
        .then(async (res: any) => {
          const stripe = (await stripePromise) as Stripe;
          const { stripe_session_id } = res.data;
          await stripe.redirectToCheckout({
            sessionId: stripe_session_id,
          });
        })
        .catch(({ response }) => {
          showToast({
            type: "error",
            title: t("errors.toast.payment", "Payment failed"),
          });
          if (errorsValidation.is400Group(response.status)) {
            formHelpers.setErrors(parseErrorsToFormik(response.data));
          }
        })
        .finally(() => {
          formHelpers.setSubmitting(false);
        });
    }
    return api.resources
      .createPaymentAddress({
        organization_codename: organization.codename,
        data: { ...values, organization: organization.id },
      })
      .then((res: any) => {
        api.resources
          .createPayment({
            organization_codename: organization.codename,
            data: {
              address_info: res.data.id,
              quantity: values.quantity,
              type: 1,
            },
          })
          .then(async (res: any) => {
            const stripe = (await stripePromise) as Stripe;
            const { stripe_session_id } = res.data;
            await stripe.redirectToCheckout({
              sessionId: stripe_session_id,
            });
          });
      })
      .catch(({ response }) => {
        showToast({
          type: "error",
          title: t("errors.toast.payment", "Payment failed"),
        });
        if (errorsValidation.is400Group(response.status)) {
          formHelpers.setErrors(parseErrorsToFormik(response.data));
        }
      })
      .finally(() => {
        formHelpers.setSubmitting(false);
      });
  };

  return (
    <StyledCardBlock>
      <Typography
        fontSize="1.25rem"
        fontWeight="500"
        color="#253858"
        mb="1.5rem"
        mt="-3rem"
      >
        {t("pages.payment.title", "Payment")}
      </Typography>
      <Notification>
        <WarnIcon />
        <Typography
          ml={"0.6rem"}
          fontSize={"0.875rem"}
          fontWeight={"500"}
          color={"#253858"}
        >
          {t(
            "payment.modal.notification1",
            "Innusificient balance. You need at least"
          )}
          {` ${totalProjectCostValue} `}
          {t(
            "payment.modal.notification2",
            " Credits to begin Data Collection."
          )}
        </Typography>
      </Notification>
      <BillingInfo
        currentBalance={limitsRes.available_tokens}
        initialValues={initialValues}
        onSubmit={onSubmit}
        onCancel={onCancel}
        addressInfoRes={addressInfoRes}
        removeAddress={removeAddress}
        onAddressChosen={setChosenAddress}
      />
    </StyledCardBlock>
  );
};

export default compose(
  connect((state) => ({
    organization: selectors.authSelectors.userOrganization(state),
    user: selectors.authSelectors.userProfile(state),
  }))
)(CreditCard);
